// The platform's one sealing envelope: every request body and every reply
// body on every family is sealed from the caller to the serving instance with
// the NATS xkey box (02-DESIGN/02-secrets/01-the-vault.md § the envelope;
// decision 0007). Subjects and headers stay clear.
//
// A service advertises its sealing key as the `sealing_key` metadata of its
// service info; a caller discovers it once, seals toward it with an ephemeral
// key it names in the Impire-Sealed-By header, and opens the reply against the
// key it sealed toward — never against a key the reply's header names, so a
// reply sealed under any other key is refused (decision 0026). Error replies
// are unsealed.
import { headers } from "@nats-io/nats-core";
import { createCurve, fromCurveSeed, fromPublic } from "@nats-io/nkeys";
import { utf8Decode } from "./encoding.js";
import { serviceInfo } from "./subject.js";
/** HEADER names the sealer's public key: the caller's on a request, the service's on a reply. */
export const HEADER = "Impire-Sealed-By";
/** METADATA_KEY is the service-info metadata entry that advertises a service's sealing key. */
export const METADATA_KEY = "sealing_key";
/** CODE_CANNOT_OPEN is the error code a service answers when it cannot open a request. */
export const CODE_CANNOT_OPEN = "cannot-open";
/** The micro error headers a service's refusal rides, never sealed. */
export const ERROR_CODE_HEADER = "Nats-Service-Error-Code";
export const ERROR_HEADER = "Nats-Service-Error";
/** DEFAULT_TIMEOUT bounds a request, in milliseconds. */
export const DEFAULT_TIMEOUT = 5000;
/**
 * ServiceError is a service's error reply: the code and message the micro
 * headers carry. It is never sealed, so a refusal is never mistaken for
 * ciphertext.
 */
export class ServiceError extends Error {
    code;
    constructor(code, message, options) {
        super(message, options);
        this.name = "ServiceError";
        this.code = code;
    }
    toString() {
        return `${this.code}: ${this.message}`;
    }
}
/** isCode reports whether err is a service error with the given code. */
export function isCode(err, code) {
    return err instanceof ServiceError && err.code === code;
}
/** CannotOpenError is thrown when an envelope does not open. */
export class CannotOpenError extends Error {
    constructor(message = "envelope: cannot open", options) {
        super(message, options);
        this.name = "CannotOpenError";
    }
}
/** validPublicKey reports whether s is an X25519 public key in nkeys encoding. */
export function validPublicKey(s) {
    if (!s.startsWith("X")) {
        return false;
    }
    try {
        fromPublic(s);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Local is a sealer whose curve key the page holds: the ephemeral key of a
 * caller, and a test's stand-in for a service's.
 */
export class Local {
    kp;
    pub;
    constructor(kp) {
        this.kp = kp;
        this.pub = kp.getPublicKey();
    }
    /** generate mints a fresh curve key. */
    static generate() {
        return new Local(createCurve());
    }
    /** fromSeed makes a sealer from a curve seed (the `SX…` form, as bytes). */
    static fromSeed(seed) {
        return new Local(fromCurveSeed(seed));
    }
    /** publicKey is the X25519 public key, in nkeys encoding, that callers seal toward. */
    publicKey() {
        return this.pub;
    }
    /** seal seals plain toward the recipient's public key. */
    seal(plain, recipient) {
        if (!validPublicKey(recipient)) {
            throw new Error("envelope: recipient is not a curve public key");
        }
        return this.kp.seal(plain, recipient);
    }
    /** open opens sealed bytes sent by the sender's public key. */
    open(sealed, sender) {
        if (!validPublicKey(sender)) {
            throw new CannotOpenError();
        }
        let out;
        try {
            out = this.kp.open(sealed, sender);
        }
        catch {
            throw new CannotOpenError();
        }
        if (out === null) {
            throw new CannotOpenError();
        }
        return out;
    }
    /** wipe forgets the key. */
    wipe() {
        this.kp.clear();
    }
}
/** sealingKeyOf reads the `sealing_key` metadata off a service-info reply. */
function sealingKeyOf(info) {
    if (typeof info !== "object" || info === null || !("metadata" in info)) {
        return undefined;
    }
    const md = info.metadata;
    if (typeof md !== "object" || md === null || !(METADATA_KEY in md)) {
        return undefined;
    }
    const key = md[METADATA_KEY];
    return typeof key === "string" ? key : undefined;
}
/**
 * Caller is the client side of the envelope: one ephemeral curve key, and a
 * cache of the sealing keys of the services it has discovered.
 */
export class Caller {
    local;
    keys = new Map();
    /** A caller mints its ephemeral key unless handed one. */
    constructor(local) {
        this.local = local ?? Local.generate();
    }
    /** publicKey is the key the caller names in the header. */
    publicKey() {
        return this.local.publicKey();
    }
    /**
     * discover reads the sealing key of the named service from `$SRV.INFO.<name>`
     * and caches it. A cached key is returned without a round trip.
     */
    async discover(nc, service, timeout = DEFAULT_TIMEOUT) {
        const cached = this.keys.get(service);
        if (cached !== undefined) {
            return cached;
        }
        let data;
        try {
            const msg = await nc.request(serviceInfo(service), undefined, { timeout });
            data = msg.data;
        }
        catch (err) {
            throw new Error(`envelope: discover ${service}: ${describe(err)}`, { cause: err });
        }
        let info;
        try {
            info = JSON.parse(utf8Decode(data));
        }
        catch (err) {
            throw new Error(`envelope: discover ${service}: ${describe(err)}`, { cause: err });
        }
        const key = sealingKeyOf(info);
        if (key === undefined || !validPublicKey(key)) {
            throw new Error(`envelope: ${service} advertises no sealing key`);
        }
        this.keys.set(service, key);
        return key;
    }
    /** forget drops a cached key so that the next request rediscovers it. */
    forget(service) {
        this.keys.delete(service);
    }
    /**
     * remember records a service's key without discovery — what a test uses to
     * plant a stale key and exercise the retry.
     */
    remember(service, key) {
        this.keys.set(service, key);
    }
    /**
     * seal seals body toward the named service's key, discovering it first when
     * it is not cached. It is the first half of request, for a caller whose
     * reply is not one message — the frame grammar's reader — which names the
     * caller's publicKey in the header itself.
     */
    async seal(nc, service, body, timeout = DEFAULT_TIMEOUT) {
        const key = await this.discover(nc, service, timeout);
        return this.local.seal(body, key);
    }
    /**
     * sealToward seals body toward a key the caller resolved itself — an
     * instance among many under one service name, whose key arrived on the
     * discovery reply that named it — touching nothing in the cache. The
     * toolkit and inference discoveries are its consumers: every toolkit is
     * the service `toolkit`, so the name alone cannot say which key.
     */
    sealToward(key, body) {
        return this.local.seal(body, key);
    }
    /**
     * open opens a reply body against the key the caller sealed the request
     * toward: the second half of request. A reply's own header never names the
     * key it is opened against (decision 0026 point 5).
     */
    open(sealed, sender) {
        return this.local.open(sealed, sender);
    }
    /**
     * request seals body toward the service's key, sends it on subj with the
     * headers, and opens the reply against that same key. A `cannot-open` refusal rediscovers the key
     * and retries once. A service error is thrown as a ServiceError.
     */
    async request(nc, service, subj, body, opts = {}) {
        const timeout = opts.timeout ?? DEFAULT_TIMEOUT;
        for (let attempt = 0;; attempt++) {
            const key = await this.discover(nc, service, timeout);
            const sealed = this.local.seal(body, key);
            const h = headers();
            if (opts.headers) {
                for (const [k, values] of opts.headers) {
                    for (const v of values) {
                        h.append(k, v);
                    }
                }
            }
            h.set(HEADER, this.publicKey());
            const resp = await nc.request(subj, sealed, { timeout, headers: h });
            const code = resp.headers?.get(ERROR_CODE_HEADER) ?? "";
            if (code !== "") {
                if (code === CODE_CANNOT_OPEN && attempt === 0) {
                    this.forget(service);
                    continue;
                }
                throw new ServiceError(code, resp.headers?.get(ERROR_HEADER) ?? "");
            }
            // Opened against the key sealed toward, whatever the header names.
            let plain;
            try {
                plain = this.local.open(resp.data, key);
            }
            catch (err) {
                throw new CannotOpenError(`envelope: reply from ${service}: cannot open`, { cause: err });
            }
            return { data: plain, headers: resp.headers };
        }
    }
}
function describe(err) {
    return err instanceof Error ? err.message : String(err);
}
//# sourceMappingURL=envelope.js.map